import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Callout, Checkbox, CodeEditor, SettingsCard, Toggle } from "@humansignal/ui";
import { useToast } from "@humansignal/ui";
import { IconCheck, IconCross } from "@humansignal/icons";
import { useUpdatePageTitle, createTitleFromSegments, useAPI } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { useBranding } from "../../providers/BrandingProvider";
import { cn } from "../../utils/bem";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { modal } from "../../components/Modal/Modal";
import { useTranslation } from "../../i18n/LocaleProvider";
// CodeMirror ships language modes as separate side-effectful imports.
// Python mode wires up the grammar + tokenizer so ``mode: "python"`` in the
// CodeEditor options actually highlights correctly.
import "codemirror/mode/python/python";

// ── Shared bits ────────────────────────────────────────────────────────────────

// Class names generated via ``cn()`` — .prefix.css files CSS-module-prefix
// every selector with ``ls-`` (see storybook/main.ts for the loader), so
// raw string class names in JSX won't match the compiled CSS. Go through
// ``cn().toClassName()`` instead so the runtime prefix is applied.
const settingsDescription = cn("settings-description").toClassName();
const settingsCaption = cn("settings-caption").toClassName();
const simpleSettings = cn("simple-settings").toClassName();
const qualityRoot = cn("quality-settings").toClassName();

const qs = (suffix) => cn(`quality-${suffix}`).toClassName();
const qsBlock = (block, element) => cn(`quality-${block}`).elem(element).toClassName();
const qsBlockMod = (block, mod) => cn(`quality-${block}`).mod(mod).toClassName();

// Inline help pill — renders next to a sub-heading and exposes a
// contextual tooltip on hover/focus. Designed to be unobtrusive: no
// modal, no state, just a ``<abbr>``-style trigger with a small bubble.
const HelpTip = ({ children, label = "Help" }) => (
  <abbr
    title={typeof children === "string" ? children : ""}
    aria-label={label}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 16,
      height: 16,
      marginLeft: 6,
      borderRadius: "50%",
      border: "1px solid var(--color-neutral-border)",
      background: "var(--color-neutral-surface-subtler, var(--color-neutral-emphasis))",
      color: "var(--color-neutral-content-subtler)",
      fontSize: "var(--font-size-body-small)",
      fontWeight: 700,
      cursor: "help",
      textDecoration: "none",
      verticalAlign: "middle",
    }}
  >
    ?
  </abbr>
);

const Section = ({ title, description, docsHref, summary, onSave, saving, dirty, headerActions, children }) => {
  const { t } = useTranslation();
  return (
    <SettingsCard
      title={title}
      description={
        description && (
          <>
            {description}
            {docsHref && (
              <>
                {" "}
                <a href={docsHref} target="_blank" rel="noreferrer">
                  {t("pages.settings.quality_settings.learn_how")}
                </a>
              </>
            )}
          </>
        )
      }
      headerActions={headerActions}
      footer={
        (summary || onSave) && (
          <>
            <span className={settingsDescription} style={{ marginRight: "auto" }}>
              {summary}
            </span>
            {onSave && (
              <Button
                style={{ width: 120 }}
                type="button"
                variant="primary"
                size="small"
                onClick={onSave}
                disabled={saving || !dirty}
                aria-label={`Save ${title}`}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </>
        )
      }
    >
      {children}
    </SettingsCard>
  );
};

// Thin adapter over the shared ``Toggle`` — matches how ReviewSettings
// wraps it. Keeps a boolean onChange API and the existing label +
// description layout so callers don't have to change.
const ToggleField = ({ checked, onChange, label, description }) => (
  <div className={qs("toggle")}>
    <Toggle label={label} description={description} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
  </div>
);

const RadioCardGroup = ({ value, onChange, options, name }) => (
  <div role="radiogroup" className={qs("radio-group")}>
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <label
          key={opt.value}
          className={`${qs("radio-card")}${active ? ` ${qsBlockMod("radio-card", { active: true })}` : ""}`}
        >
          <input type="radio" name={name} value={opt.value} checked={active} onChange={() => onChange(opt.value)} />
          <div>
            <div className={qsBlock("radio-card", "title")}>
              <span>{opt.title}</span>
              {opt.applies && <span className={qsBlock("radio-card", "tag")}>{opt.applies}</span>}
            </div>
            {opt.description && <div className={qsBlock("radio-card", "desc")}>{opt.description}</div>}
          </div>
        </label>
      );
    })}
  </div>
);

const SliderRow = ({
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.05,
  formatValue = (v) => `${Math.round(v * 100)}%`,
}) => (
  <div className={qs("slider-row")}>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />
    <span className={qs("slider-value")}>{formatValue(value)}</span>
  </div>
);

// ── 1. Overlap ─────────────────────────────────────────────────────────────────

const OverlapSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [annotations, setAnnotations] = useState(project.maximum_annotations ?? 1);
  const [percent, setPercent] = useState(project.overlap_cohort_percentage ?? 100);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAnnotations(project.maximum_annotations ?? 1);
    setPercent(project.overlap_cohort_percentage ?? 100);
  }, [project.id, project.maximum_annotations, project.overlap_cohort_percentage]);

  const dirty = annotations !== project.maximum_annotations || percent !== project.overlap_cohort_percentage;

  const save = useCallback(async () => {
    setSaving(true);
    const res = await updateProject({
      maximum_annotations: Number(annotations) || 1,
      overlap_cohort_percentage: Math.max(0, Math.min(100, Number(percent) || 0)),
    });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.quality_settings.overlap_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.quality_settings.failed_to_save_overlap"), type: "error" });
  }, [annotations, percent, updateProject, toast, onSaved]);

  const showCohort = annotations > 1;
  const summary =
    annotations <= 1
      ? "A single annotation is required for all tasks."
      : `${annotations} annotations required for ${percent}% of tasks.`;

  return (
    <Section
      title={t("pages.settings.quality_settings.overlap_of_annotations")}
      description={t("pages.settings.quality_settings.define_how_many_annotations_are_required")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <div className={qs("field")}>
        {/* Renamed from "Annotations per task" so it doesn't collide
            with the Annotation-settings field of the same name. This
            one controls overlap (how many DIFFERENT annotators must
            label each task); the other controls how many regions a
            single annotator must drop per task. Naming it "Annotators
            per task" makes that distinction read at a glance. */}
        <label className={qsBlock("field", "label")}>{t("pages.settings.quality_settings.annotators_per_task")}</label>
        <p className={settingsCaption} style={{ marginTop: 0 }}>
          {t("pages.settings.quality_settings.number_of_independent_annotators_require")}
        </p>
        <input
          type="number"
          min={1}
          className={qs("input")}
          value={annotations}
          onChange={(e) => setAnnotations(Math.max(1, Number(e.target.value) || 1))}
        />
      </div>
      {showCohort && (
        <div className={qs("field")}>
          <label className={qsBlock("field", "label")}>
            {t("pages.settings.quality_settings.apply_to_this_of_tasks")}
          </label>
          <p className={settingsCaption} style={{ marginTop: 0 }}>
            Remaining tasks keep a single annotation — useful when overlap is only needed for a sampled subset.
          </p>
          <SliderRow value={percent} onChange={setPercent} min={0} max={100} step={5} formatValue={(v) => `${v}%`} />
        </div>
      )}
    </Section>
  );
};

// ── 2. Tasks Per Annotator Limit ───────────────────────────────────────────────

const TasksPerAnnotatorSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const initialEnabled = project.tasks_per_annotator_limit != null;
  const [enabled, setEnabled] = useState(initialEnabled);
  const [limit, setLimit] = useState(project.tasks_per_annotator_limit ?? 50);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const on = project.tasks_per_annotator_limit != null;
    setEnabled(on);
    setLimit(project.tasks_per_annotator_limit ?? 50);
  }, [project.id, project.tasks_per_annotator_limit]);

  const effective = enabled ? Math.max(1, Number(limit) || 1) : null;
  const dirty = effective !== (project.tasks_per_annotator_limit ?? null);

  const save = useCallback(async () => {
    setSaving(true);
    const res = await updateProject({ tasks_per_annotator_limit: effective });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.quality_settings.task_limit_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.quality_settings.failed_to_save_limit"), type: "error" });
  }, [effective, updateProject, toast, onSaved]);

  const summary =
    effective == null
      ? "No limits currently configured."
      : `Each annotator may claim up to ${effective} task${effective === 1 ? "" : "s"}.`;

  return (
    <Section
      title={t("pages.settings.quality_settings.tasks_per_annotator_limit")}
      description={t("pages.settings.quality_settings.control_the_number_of_tasks_each_annotat")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={enabled}
        onChange={setEnabled}
        label={t("pages.settings.quality_settings.limit_tasks_per_annotator")}
        description={t("pages.settings.quality_settings.enable_to_configure_limits")}
      />
      {enabled && (
        <div className={qs("field")} style={{ marginTop: 12 }}>
          <label className={qsBlock("field", "label")}>
            {t("pages.settings.quality_settings.max_tasks_per_annotator")}
          </label>
          <input
            type="number"
            min={1}
            className={qs("input")}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      )}
    </Section>
  );
};

// ── 3. Annotator Evaluation ────────────────────────────────────────────────────

const EvaluationSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [enabled, setEnabled] = useState(!!project.annotator_evaluation_enabled);
  const [countsReviews, setCountsReviews] = useState(!!project.evaluation_counts_reviews);
  const [minScore, setMinScore] = useState(project.evaluation_min_score ?? 0.8);
  const [minGT, setMinGT] = useState(project.evaluation_min_ground_truth ?? 5);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!project.annotator_evaluation_enabled);
    setCountsReviews(!!project.evaluation_counts_reviews);
    setMinScore(project.evaluation_min_score ?? 0.8);
    setMinGT(project.evaluation_min_ground_truth ?? 5);
  }, [
    project.id,
    project.annotator_evaluation_enabled,
    project.evaluation_min_score,
    project.evaluation_min_ground_truth,
  ]);

  const dirty =
    enabled !== !!project.annotator_evaluation_enabled ||
    (enabled && countsReviews !== !!project.evaluation_counts_reviews) ||
    (enabled && minScore !== (project.evaluation_min_score ?? 0.8)) ||
    (enabled && minGT !== (project.evaluation_min_ground_truth ?? 5));

  const save = useCallback(async () => {
    setSaving(true);
    const body = { annotator_evaluation_enabled: enabled };
    if (enabled) {
      body.evaluation_counts_reviews = countsReviews;
      body.evaluation_min_score = Math.max(0, Math.min(1, Number(minScore) || 0));
      body.evaluation_min_ground_truth = Math.max(0, Number(minGT) || 0);
    } else {
      body.evaluation_min_score = null;
    }
    const res = await updateProject(body);
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.quality_settings.evaluation_settings_saved") });
      onSaved?.();
    } else toast.show({ message: "Failed to save", type: "error" });
  }, [enabled, minScore, minGT, updateProject, toast, onSaved]);

  const summary = !enabled
    ? "Annotators will not be evaluated."
    : `Annotators must reach ≥${Math.round(minScore * 100)}% agreement on ≥${minGT} ground-truth task${minGT === 1 ? "" : "s"} to stay unpaused.`;

  return (
    <Section
      title={t("pages.settings.quality_settings.annotator_evaluation")}
      description={t("pages.settings.quality_settings.evaluate_annotators_against_ground_truth")}
      docsHref="https://afriannotate.waraka.org/docs/review-and-quality/annotator-evaluation"
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={enabled}
        onChange={setEnabled}
        label={t("pages.settings.quality_settings.evaluate_all_annotators_against_ground_t")}
        description={t("pages.settings.quality_settings.enable_to_require_all_annotators_to_comp")}
      />
      {enabled && (
        <ToggleField
          checked={countsReviews}
          onChange={setCountsReviews}
          label="Count reviewer verdicts too"
          description="Accepted or fixed annotations score 1, rejected ones 0, alongside ground-truth comparisons. Off: evaluation is ground-truth only."
        />
      )}
      {enabled && (
        <div className={qs("row")} style={{ marginTop: 12 }}>
          <div className={qs("field")}>
            <label className={qsBlock("field", "label")}>
              {t("pages.settings.quality_settings.minimum_agreement_score")}
            </label>
            <SliderRow value={minScore} onChange={setMinScore} min={0} max={1} step={0.05} />
          </div>
          <div className={qs("field")}>
            <label className={qsBlock("field", "label")}>
              {t("pages.settings.quality_settings.groundtruth_tasks_required")}
            </label>
            <input
              type="number"
              min={0}
              className={qs("input")}
              value={minGT}
              onChange={(e) => setMinGT(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>
        </div>
      )}
    </Section>
  );
};

// ── 4. Agreement ───────────────────────────────────────────────────────────────

const AGREEMENT_OPTIONS = [
  {
    value: "pairwise",
    title: "Pairwise averaging",
    description:
      "Averages the selected metric across all annotator pairs for a task. Good for spotting disagreement and tracking overall consistency.",
  },
  {
    value: "consensus",
    title: "Consensus (plurality)",
    description:
      'Per field, counts how many annotators landed on the plurality answer, averaged across fields. Best when you want "did the group converge?" as a single number.',
  },
  {
    value: "cohens_kappa",
    title: "Cohen's kappa",
    description:
      "Chance-corrected agreement between two annotators, averaged across every pair when there are more than two. Uses each annotator's own marginals, so picks up rater-specific bias.",
  },
  {
    value: "fleiss_kappa",
    title: "Fleiss' kappa",
    description:
      "Cohen's kappa generalised to more than two annotators. Shared marginals across the group, the standard choice for >2-annotator classification projects.",
  },
  {
    value: "randolph_kappa",
    title: "Randolph's free-marginal kappa",
    description:
      "Fleiss' kappa with the chance-agreement floor fixed at 1/q (number of categories) instead of empirical marginals. Robust when one category is over-represented.",
  },
  {
    value: "krippendorff_alpha",
    title: "Krippendorff's alpha",
    description:
      "Handles missing answers natively and works across nominal data. Prefer this when not every annotator labelled every field.",
  },
  {
    value: "scotts_pi",
    title: "Scott's pi",
    description:
      "Two-annotator chance correction with a pooled marginal distribution, averaged across pairs for >2-annotator projects. Useful when both annotators are drawn from the same pool.",
  },
  {
    value: "gwet_ac1",
    title: "Gwet's AC1",
    description:
      'Chance-corrected agreement that stays robust when one category dominates, avoids the "kappa paradox" where high observed agreement collapses to a near-zero kappa on imbalanced labels (e.g. skewed sentiment or toxicity).',
  },
  {
    value: "gwet_ac2",
    title: "Gwet's AC2 (ordinal)",
    description:
      "Gwet's AC1 extended with distance weights for ordered numeric labels (Rating / Number), near misses count as partial agreement. Falls back gracefully when a field isn't on a numeric scale.",
  },
  {
    value: "weighted_kappa_linear",
    title: "Weighted kappa (linear)",
    description:
      "Cohen's kappa for ordered numeric labels (Rating / Number) where disagreement is penalised in proportion to how far apart the ratings are, a 4-vs-5 counts as closer than a 1-vs-5.",
  },
  {
    value: "weighted_kappa_quadratic",
    title: "Weighted kappa (quadratic)",
    description:
      "Weighted kappa with squared distance penalties, the standard choice for ordinal quality ratings and audio MOS scores, where large disagreements should be punished much harder than small ones.",
  },
];

// One-line "what this needs" badge per methodology — surfaces the rater-count
// and data-type constraints so it's obvious e.g. that kappa is a categorical,
// multi-rater statistic and that weighted kappa needs an ordinal numeric scale.
const METHOD_APPLIES = {
  pairwise: "Any data, uses the metric",
  consensus: "Any data, plurality vote",
  cohens_kappa: "2 raters, categorical",
  fleiss_kappa: ">2 raters, categorical",
  randolph_kappa: "≥2 raters, categorical",
  krippendorff_alpha: "≥2 raters, categorical, handles gaps",
  scotts_pi: "2 raters, categorical",
  gwet_ac1: "≥2 raters, categorical, imbalance-robust",
  gwet_ac2: "≥2 raters, ordinal (numeric)",
  weighted_kappa_linear: "2 raters, ordinal (numeric)",
  weighted_kappa_quadratic: "2 raters, ordinal (numeric)",
};

// Chance-corrected coefficients work directly on the labels and IGNORE the
// per-pair metric — only "pairwise" (and loosely "consensus") consume it.
const COEFFICIENT_METHODS = new Set([
  "cohens_kappa",
  "fleiss_kappa",
  "randolph_kappa",
  "krippendorff_alpha",
  "scotts_pi",
  "gwet_ac1",
  "gwet_ac2",
  "weighted_kappa_linear",
  "weighted_kappa_quadratic",
]);
const usesMetric = (method) => !COEFFICIENT_METHODS.has(method);

// ── Schema reference dialog ───────────────────────────────────────────────────
//
// Shown on demand instead of an inline accordion so the code editor keeps
// the full page width. Escape / backdrop-click both close. Scrollable body
// because a project with many controls would otherwise blow past the
// viewport height.

const SchemaDialog = ({ schema, onClose, initialTab = "controls" }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const controls = schema?.controls ?? [];

  // Inline everything — modal layout is fiddly and depending on the
  // .prefix.css pipeline has burned us before on this section.
  const tabBtn = (key, label) => {
    const active = tab === key;
    return (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setTab(key)}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          padding: "10px 16px",
          marginBottom: -1,
          borderBottom: active ? "2px solid var(--color-primary-surface)" : "2px solid transparent",
          fontSize: "var(--font-size-label)",
          fontWeight: active ? 600 : 500,
          color: active ? "var(--color-primary-surface)" : "var(--color-neutral-content-subtler)",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Available fields"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        padding: 24,
        background: "rgba(15, 17, 27, 0.48)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          // Static height so switching tabs doesn't resize the dialog
          // around the user. 72vh leaves generous padding on most screens.
          width: "min(720px, 100%)",
          height: "72vh",
          background: "var(--color-neutral-background)",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.28)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 20px 10px",
          }}
        >
          <h2 style={{ margin: 0, fontWeight: 600 }}>{t("pages.settings.quality_settings.available_fields")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              borderRadius: 99,
              border: "none",
              background: "transparent",
              color: "var(--color-neutral-content-subtler)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconCross style={{ width: 14, height: 14 }} />
          </button>
        </header>

        <div
          role="tablist"
          style={{
            display: "flex",
            gap: 2,
            padding: "0 16px",
            borderBottom: "1px solid var(--color-neutral-border)",
          }}
        >
          {tabBtn("controls", `Controls${controls.length ? `, ${controls.length}` : ""}`)}
          {tabBtn("sample", "Sample annotation")}
          {tabBtn("rules", "Sandbox rules")}
        </div>

        <div style={{ padding: "14px 20px 20px", overflowY: "auto", flex: 1 }}>
          {tab === "controls" ? (
            controls.length === 0 ? (
              <p
                style={{ margin: 0, fontSize: "var(--font-size-label)", color: "var(--color-neutral-content-subtler)" }}
              >
                Your labeling config doesn't declare any controls yet — use the sample annotation tab to see the generic
                result shape.
              </p>
            ) : (
              <ul
                style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}
              >
                {controls.map((c) => (
                  <li
                    key={c.name}
                    style={{
                      padding: "10px 12px",
                      border: "1px solid var(--color-neutral-border)",
                      borderRadius: 8,
                      background: "var(--color-neutral-background)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          fontSize: "var(--font-size-label)",
                          fontWeight: 600,
                          color: "var(--color-neutral-content)",
                        }}
                      >
                        {c.name}
                      </span>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "1px 8px",
                          borderRadius: 99,
                          background: "rgba(37, 99, 235, 0.12)",
                          color: "var(--color-primary-surface)",
                          fontSize: "var(--font-size-body-small)",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {c.type}
                      </span>
                    </div>
                    {c.labels?.length > 0 && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: "var(--font-size-body-small)",
                          color: "var(--color-neutral-content-subtler)",
                        }}
                      >
                        {c.labels.slice(0, 8).join(", ")}
                        {c.labels.length > 8 ? ", …" : ""}
                      </div>
                    )}
                    <pre
                      style={{
                        margin: "8px 0 0",
                        padding: "8px 10px",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        fontSize: "var(--font-size-body-small)",
                        lineHeight: 1.5,
                        color: "var(--color-neutral-content)",
                        background: "var(--color-neutral-emphasis)",
                        borderRadius: 6,
                        overflowX: "auto",
                        whiteSpace: "pre",
                      }}
                    >
                      {JSON.stringify(c.sample, null, 2)}
                    </pre>
                  </li>
                ))}
              </ul>
            )
          ) : tab === "sample" ? (
            <>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "var(--font-size-label)",
                  color: "var(--color-neutral-content-subtler)",
                }}
              >
                Shape of <code>annotation_1</code> / <code>annotation_2</code> passed into your function.
              </p>
              <pre
                style={{
                  margin: 0,
                  padding: "10px 12px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: "var(--font-size-body-small)",
                  lineHeight: 1.5,
                  color: "var(--color-neutral-content)",
                  background: "var(--color-neutral-emphasis)",
                  borderRadius: 8,
                  overflowX: "auto",
                  whiteSpace: "pre",
                }}
              >
                {JSON.stringify(schema?.sample_annotation ?? {}, null, 2)}
              </pre>
            </>
          ) : (
            // Sandbox rules tab — what the AST gate lets through and what
            // it rejects. Content duplicated from the server-side
            // ``custom_agreement.py`` so it's accurate per deployment.
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                fontSize: "var(--font-size-label)",
                lineHeight: 1.55,
              }}
            >
              <div>
                <strong>{t("pages.settings.quality_settings.signature")}</strong>
                <pre
                  style={{
                    margin: "6px 0 0",
                    padding: "8px 10px",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    fontSize: "var(--font-size-body-small)",
                    color: "var(--color-neutral-content)",
                    background: "var(--color-neutral-emphasis)",
                    borderRadius: 6,
                    whiteSpace: "pre",
                  }}
                >
                  {`def agreement(annotation_1, annotation_2, per_label=False) -> float:
    ...`}
                </pre>
              </div>

              <div>
                <strong>{t("pages.settings.quality_settings.return_value")}</strong>
                <p style={{ margin: "4px 0 0", color: "var(--color-neutral-content-subtler)" }}>
                  A float in <code>[0, 1]</code>. Higher means more agreement. Values outside the range are clamped.
                  Raise or return <code>{t("pages.settings.quality_settings.none")}</code> to skip this pair.
                </p>
              </div>

              <div>
                <strong>{t("pages.settings.quality_settings.allowed_imports")}</strong>
                <p style={{ margin: "4px 0 6px", color: "var(--color-neutral-content-subtler)" }}>
                  Only pure-Python stdlib modules:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    "math",
                    "statistics",
                    "itertools",
                    "functools",
                    "collections",
                    "collections.abc",
                    "json",
                    "re",
                    "string",
                    "datetime",
                    "fractions",
                    "decimal",
                    "random",
                    "heapq",
                    "bisect",
                    "operator",
                    "textwrap",
                    "difflib",
                    "unicodedata",
                  ].map((m) => (
                    <code
                      key={m}
                      style={{
                        padding: "2px 8px",
                        borderRadius: 99,
                        background: "rgba(37, 99, 235, 0.1)",
                        color: "var(--color-primary-surface)",
                        fontSize: "var(--font-size-body-small)",
                      }}
                    >
                      {m}
                    </code>
                  ))}
                </div>
              </div>

              <div>
                <strong>{t("pages.settings.quality_settings.rejected_at_parse_time")}</strong>
                <ul style={{ margin: "4px 0 0 18px", padding: 0, color: "var(--color-neutral-content-subtler)" }}>
                  <li>
                    <code>import os</code> / <code>subprocess</code> / <code>socket</code> / anything outside the
                    whitelist
                  </li>
                  <li>
                    {t("pages.settings.quality_settings.attribute_chains_into_dunders_eg")} <code>obj.__class__</code>)
                  </li>
                  <li>
                    {t("pages.settings.quality_settings.calls_to")} <code>exec</code>, <code>eval</code>,{" "}
                    <code>compile</code>, <code>open</code>, <code>getattr</code>, <code>globals</code>
                  </li>
                  <li>
                    <code>with</code> {t("pages.settings.quality_settings.statements")} <code>async</code>/
                    <code>await</code>
                    {t("pages.settings.quality_settings.class_definitions_decorators")}
                  </li>
                </ul>
              </div>

              <div>
                <strong>{t("pages.settings.quality_settings.runtime_limits")}</strong>
                <ul style={{ margin: "4px 0 0 18px", padding: 0, color: "var(--color-neutral-content-subtler)" }}>
                  <li>{t("pages.settings.quality_settings.cpu_capped_at_2_seconds_per_pairwise_com")}</li>
                  <li>{t("pages.settings.quality_settings.any_exception_the_pair_is_skipped_and_lo")}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Inline-style helper for the Metric → Methodology → Score badge strip in
// the Agreement explainer. Three tones so each stage reads differently.
const badgeFlowStyle = (tone) => {
  const common = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 99,
    fontSize: "var(--font-size-badge)",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  };
  if (tone === "metric")
    return { ...common, background: "rgba(37, 99, 235, 0.14)", color: "var(--color-primary-surface)" };
  if (tone === "method") return { ...common, background: "rgba(130, 80, 223, 0.14)", color: "#6f2cba" };
  return { ...common, background: "rgba(31, 122, 90, 0.16)", color: "var(--color-positive-content, #1f7a5a)" };
};

// Inline-style helper for the metric compatibility check — ``ok`` is the
// positive / green variant, ``warn`` is the amber blocking variant.
const checkBoxStyle = (variant) => ({
  marginTop: 8,
  padding: "10px 12px",
  borderRadius: 8,
  fontSize: "var(--font-size-label)",
  lineHeight: 1.5,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  ...(variant === "ok"
    ? {
        background: "rgba(31, 122, 90, 0.08)",
        border: "1px solid rgba(31, 122, 90, 0.28)",
        color: "var(--color-positive-content, #1f7a5a)",
      }
    : { background: "rgba(245, 166, 35, 0.12)", border: "1px solid rgba(245, 166, 35, 0.32)", color: "#8a5a00" }),
});

// Shared pill-button styling for the header callouts (Available fields /
// Sandbox rules). Inline so it stays resilient to .prefix.css churn.
const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  border: "1px solid var(--color-neutral-border)",
  borderRadius: 999,
  background: "var(--color-neutral-background)",
  color: "var(--color-neutral-content)",
  font: "inherit",
  fontSize: "var(--font-size-label)",
  cursor: "pointer",
};

// Template for the starter snippet. The ``__APP__`` placeholder is filled
// in at render time with the branding-provider's app name so the comment
// reflects the running deployment, not the upstream project.
const CUSTOM_STARTER_TEMPLATE = `# __APP__ custom agreement metric.
# Return a float in [0, 1], higher = more agreement.
# See /docs/custom_metric for the annotation JSON shape.

def agreement(annotation_1, annotation_2, per_label=False) -> float:
    r1 = annotation_1["result"]
    r2 = annotation_2["result"]

    # Example: exact match on a single Choices control named "sentiment".
    try:
        c1 = r1[0]["value"]["choices"][0]
        c2 = r2[0]["value"]["choices"][0]
    except (IndexError, KeyError, TypeError):
        return 0.0

    return 1.0 if c1 == c2 else 0.0
`;

const AgreementSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  // Resolve the app-name from the branding provider so copy doesn't lie
  // about which product the operator is using. Falls back to a generic
  // capitalisation if branding hasn't loaded yet.
  const branding = useBranding();
  const appName = useMemo(() => {
    const raw = (branding?.name ?? "").trim();
    if (!raw) return "the platform";
    return raw.replace(/\b\w/g, (c) => c.toUpperCase());
  }, [branding?.name]);

  // Server-side state we care about.
  const initial = {
    method: project.agreement_method ?? "pairwise",
    metric: project.agreement_metric ?? "",
    code: project.agreement_custom_code ?? "",
    embUrl: project.embedding_api_url ?? "",
    embToken: project.embedding_api_token ?? "",
    embTimeout: project.embedding_api_timeout_ms ?? 3000,
  };
  const [method, setMethod] = useState(initial.method);
  const [metric, setMetric] = useState(initial.metric);
  // Embedding endpoint config — surfaces as a dedicated card when a
  // semantic metric is selected.
  const [embUrl, setEmbUrl] = useState(initial.embUrl);
  const [embToken, setEmbToken] = useState(initial.embToken);
  const [embTimeout, setEmbTimeout] = useState(initial.embTimeout);
  const [embHealth, setEmbHealth] = useState(null);
  const [embTesting, setEmbTesting] = useState(false);
  const starter = useMemo(() => CUSTOM_STARTER_TEMPLATE.replace("__APP__", appName), [appName]);
  const [code, setCode] = useState(initial.code || starter);
  const [saving, setSaving] = useState(false);

  // Agreement definition is a modal on demand — the explainer is big and
  // interrupts the settings flow when inline. Operators hit the button when
  // they want the "what does this number mean?" recap.
  const openAgreementDefinition = useCallback(() => {
    modal({
      title: "What is agreement?",
      style: { width: 560 },
      body: () => (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-base)" }}>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-body-small)",
              lineHeight: "var(--line-height-body)",
              color: "var(--color-neutral-content)",
            }}
          >
            <strong>{t("pages.settings.quality_settings.agreement")}</strong>{" "}
            {t("pages.settings.quality_settings.is_a_single_number_in")} <code>[0, 1]</code> that says how much your
            annotators agreed on a task. It's produced in two steps:
          </p>
          <ol
            style={{
              margin: 0,
              paddingLeft: "var(--spacing-wide)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-tight)",
              fontSize: "var(--font-size-body-small)",
              lineHeight: "var(--line-height-body)",
              color: "var(--color-neutral-content)",
            }}
          >
            <li>
              <strong>{t("pages.settings.quality_settings.metric")}</strong> {t("pages.settings.quality_settings.how")}{" "}
              <em>two</em> annotations on the same task are compared. Examples: exact match, Jaccard on Choices, IoU on
              Rectangles, edit distance / WER / chrF on TextArea, or your own Python.
            </li>
            <li>
              <strong>{t("pages.settings.quality_settings.methodology")}</strong>. How a whole task (and the project)
              gets one number. <em>Pairwise</em> and <em>Consensus</em> build on the metric above. The chance-corrected{" "}
              <em>coefficients</em> (Cohen's / Fleiss' / Scott's / Randolph's kappa, Krippendorff's alpha, Gwet's
              AC1/AC2, weighted kappa) instead compare the labels directly, they're categorical (or, for AC2 / weighted
              kappa, ordinal numeric) statistics and <strong>ignore the metric</strong>.
            </li>
          </ol>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-tighter)",
              fontSize: "var(--font-size-body-small)",
              color: "var(--color-neutral-content-subtler)",
              flexWrap: "wrap",
            }}
          >
            <span style={badgeFlowStyle("metric")}>Metric</span>
            <span aria-hidden>→</span>
            <span style={badgeFlowStyle("method")}>Methodology</span>
            <span aria-hidden>→</span>
            <span style={badgeFlowStyle("score")}>{t("pages.settings.quality_settings.agreement_score")}</span>
          </div>
        </div>
      ),
    });
  }, []);
  const [recomputing, setRecomputing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // The dialog carries three tabs (Controls / Sample / Rules). The header
  // buttons each open it on a different default tab, stored here.
  const [schemaOpen, setSchemaOpen] = useState(null);

  // Metric catalogue + label-config schema come from the backend so the
  // dropdown and the "Available fields" panel are always in sync with the
  // server-side registry. Both are cheap idempotent reads.
  const [catalogue, setCatalogue] = useState(null);
  const [schema, setSchema] = useState(null);
  const loadedFor = useRef(null);

  useEffect(() => {
    if (!project?.id || loadedFor.current === project.id) return;
    loadedFor.current = project.id;
    api.callApi("projectQualityMetrics", { params: { pk: project.id }, suppressError: true }).then((res) => {
      if (res && !res.error) setCatalogue(res);
    });
    api.callApi("projectQualitySchema", { params: { pk: project.id }, suppressError: true }).then((res) => {
      if (res && !res.error) setSchema(res);
    });
  }, [api, project?.id]);

  useEffect(() => {
    setMethod(project.agreement_method ?? "pairwise");
    setMetric(project.agreement_metric ?? "");
    setCode(project.agreement_custom_code || starter);
  }, [project.id, project.agreement_method, project.agreement_metric, project.agreement_custom_code]);

  const isSemantic = metric === "semantic_similarity" || metric === "semantic_similarity_threshold";

  // Single source of truth for "is the selected built-in metric able to
  // score anything on this label config?" — used to gate Save /
  // Recompute so operators can't ship a config that would simply produce
  // blank scores.
  const metricCompatibility = useMemo(() => {
    if (!metric || metric === "custom") return { ok: true };
    const list = catalogue?.metrics ?? [];
    const spec = list.find((m) => m.key === metric);
    const appliesTo = spec?.applies_to ?? [];
    if (appliesTo.includes("*")) return { ok: true };
    const controls = schema?.controls ?? [];
    if (controls.length === 0) {
      return { ok: false, reason: "Add at least one control tag to the labeling config." };
    }
    const matches = controls.filter((c) => appliesTo.includes(c.type));
    if (matches.length === 0) {
      return {
        ok: false,
        reason: `This metric needs a ${appliesTo.join(" / ")} control; your config has ${controls.map((c) => c.type).join(", ")}.`,
      };
    }
    return { ok: true };
  }, [metric, catalogue, schema]);

  const dirty =
    method !== initial.method ||
    (metric || "") !== (initial.metric || "") ||
    (metric === "custom" && code !== initial.code) ||
    (isSemantic &&
      (embUrl !== initial.embUrl ||
        embToken !== initial.embToken ||
        Number(embTimeout) !== Number(initial.embTimeout)));

  const save = useCallback(async () => {
    // Defence-in-depth: the button should already be disabled, but a
    // keyboard submit could still fire — refuse politely.
    if (!metricCompatibility.ok) {
      toast.show({
        message: `Cannot save: ${metricCompatibility.reason}`,
        type: "error",
      });
      return;
    }
    setSaving(true);
    const body = {
      agreement_method: method,
      agreement_metric: metric || null,
    };
    if (metric === "custom") body.agreement_custom_code = code;
    if (isSemantic) {
      body.embedding_api_url = embUrl.trim();
      body.embedding_api_token = embToken;
      body.embedding_api_timeout_ms = Number(embTimeout) || 3000;
    }
    const res = await updateProject(body);
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.quality_settings.agreement_settings_saved") });
      onSaved?.();
    } else toast.show({ message: "Failed to save", type: "error" });
  }, [
    method,
    metric,
    code,
    isSemantic,
    embUrl,
    embToken,
    embTimeout,
    metricCompatibility,
    updateProject,
    toast,
    onSaved,
  ]);

  const testEmbedding = useCallback(async () => {
    if (!embUrl.trim()) return;
    setEmbTesting(true);
    setEmbHealth(null);
    const res = await api.callApi("projectQualityEmbeddingHealth", {
      params: { pk: project.id },
      body: {
        url: embUrl.trim(),
        token: embToken,
        timeout_ms: Number(embTimeout) || 3000,
      },
      suppressError: true,
    });
    setEmbTesting(false);
    if (!res || res.error) {
      setEmbHealth({ ok: false, error: res?.error ?? "Request failed" });
    } else {
      setEmbHealth(res);
    }
  }, [api, project?.id, embUrl, embToken, embTimeout]);

  const [recomputeResult, setRecomputeResult] = useState(null);

  const recompute = useCallback(async () => {
    setRecomputing(true);
    setRecomputeResult(null);
    const res = await api.callApi("projectQualityRecompute", {
      params: { pk: project.id },
      body: {},
      suppressError: true,
    });
    setRecomputing(false);
    if (res && !res.error) {
      setRecomputeResult(res);
      // Short toast; the detail card below carries the full breakdown.
      toast.show({ message: `Recomputed ${res.scanned} task(s). ${res.scored} scored.` });
    } else {
      setRecomputeResult({ error: res?.error ?? "Recompute failed" });
      toast.show({ message: res?.error ?? "Recompute failed", type: "error" });
    }
  }, [api, project?.id, toast]);

  const testRun = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    const res = await api.callApi("projectQualityTest", {
      params: { pk: project.id },
      body: { code },
      suppressError: true,
    });
    setTesting(false);
    if (!res || res.error) {
      setTestResult({ ok: false, error: res?.error ?? "Request failed" });
      return;
    }
    setTestResult(res);
  }, [api, project?.id, code]);

  // Build dropdown entries — registry + sentinel + 'auto'. Unapplicable
  // registry items are shown but disabled so operators understand why a
  // metric from the docs doesn't apply to their label config.
  const metrics = catalogue?.metrics ?? [];
  // Every metric is selectable on any project — the dropdown just groups
  // "likely a good fit for your label config" above "other metrics" so the
  // common cases surface first without hiding anything.
  const applicable = metrics.filter((m) => m.recommended ?? m.applicable);
  const nonApplicable = metrics.filter((m) => !(m.recommended ?? m.applicable));

  const isCustom = metric === "custom";
  const hasConfig = (schema?.controls?.length ?? 0) > 0;
  const activeMetricLabel =
    metric === "custom"
      ? "Custom Python"
      : metric === ""
        ? "Auto (pick per control)"
        : (metrics.find((m) => m.key === metric)?.label ?? metric);

  const summary = `${method === "consensus" ? "Consensus" : "Pairwise"}, ${activeMetricLabel}`;

  return (
    <Section
      title="Agreement"
      description={t("pages.settings.quality_settings.choose_how_agreement_is_calculated_for_t")}
      docsHref="https://afriannotate.waraka.org/docs/review-and-quality/agreement"
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty && metricCompatibility.ok}
      headerActions={
        <Button type="button" look="string" size="small" onClick={openAgreementDefinition}>
          What is agreement?
        </Button>
      }
    >
      {/* Step 1 — the metric picker comes first because the operator's
          first decision is "how do I compare two annotations?". The
          methodology (how to roll up across N annotators) only matters
          once the pairwise comparison is defined. */}
      <h3 style={{ margin: "0 0 4px" }}>
        <span style={{ ...badgeFlowStyle("metric"), marginRight: 8 }}>
          {t("pages.settings.quality_settings.step_1")}
        </span>
        Metric
        <HelpTip>
          How two annotations on the same task are compared. Examples: exact match, Jaccard, IoU, edit distance. Custom
          Python is the escape hatch when a built-in doesn't fit.
        </HelpTip>
      </h3>
      <p className={settingsDescription} style={{ marginTop: 0 }}>
        Pairwise comparison function — how similar are two annotations.
      </p>

      {!usesMetric(method) && (
        <div className={qs("metric-ignored")}>
          Your scoring method — <strong>{AGREEMENT_OPTIONS.find((o) => o.value === method)?.title}</strong> is a
          chance-corrected coefficient that compares the labels directly, so this metric isn’t used while it’s selected.
          Pick <strong>Pairwise averaging</strong> below if you want a metric to drive the score.
        </div>
      )}

      {/* Metric picker: header + tabs + active-tab body all live inside one
          bordered card so the three pieces read as a single "metric mode"
          component instead of three loose sections. */}
      <div
        style={{
          border: "1px solid var(--color-neutral-border)",
          borderRadius: 10,
          background: "var(--color-neutral-background)",
          overflow: "hidden",
          marginTop: 8,
        }}
      >
        {/* Recompute-result breakdown. Surfaces the three buckets separately
          so operators can tell "not enough annotators on the task" apart
          from "my metric doesn't apply to this shape" — the latter means
          the metric choice is wrong, not that the project needs more
          labeling work. */}
        {recomputeResult &&
          !recomputeResult.error &&
          (() => {
            const { scanned = 0, scored = 0, mismatch = 0, skipped = 0 } = recomputeResult;
            const warn = mismatch > 0 && mismatch >= Math.max(1, scored);
            return (
              <div
                style={{
                  margin: "0 18px 14px",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid " + (warn ? "rgba(245, 166, 35, 0.32)" : "rgba(31, 122, 90, 0.28)"),
                  background: warn ? "rgba(245, 166, 35, 0.10)" : "rgba(31, 122, 90, 0.06)",
                  color: warn ? "#8a5a00" : "var(--color-positive-content, #1f7a5a)",
                  fontSize: "var(--font-size-label)",
                  lineHeight: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 14,
                  alignItems: "center",
                }}
              >
                <span>
                  <strong>{scored}</strong> scored
                </span>
                {mismatch > 0 && (
                  <span title={t("pages.settings.quality_settings.task_had_2_annotations_but_the_selected_")}>
                    <strong>{mismatch}</strong> no shape match
                  </span>
                )}
                {skipped > 0 && (
                  <span title={t("pages.settings.quality_settings.task_had_fewer_than_2_eligible_annotatio")}>
                    <strong>{skipped}</strong> waiting for more annotators
                  </span>
                )}
                <span style={{ marginLeft: "auto", opacity: 0.85 }}>
                  {scanned} task{scanned === 1 ? "" : "s"} scanned
                </span>
              </div>
            );
          })()}

        {/* Source-of-truth picker: a tab strip with three modes —
          Auto / Built-in / Custom. Only the active tab's body renders,
          so the section stays tall-ish only when the operator is actually
          editing custom code. */}
        {(() => {
          const activeTab = metric === "custom" ? "custom" : metric ? "builtin" : "auto";
          const tabs = [
            { key: "auto", label: "Auto" },
            { key: "builtin", label: t("pages.settings.quality_settings.builtin") },
            { key: "custom", label: t("pages.settings.quality_settings.custom_python") },
          ];
          const switchTab = (key) => {
            if (key === "auto") setMetric("");
            else if (key === "custom") setMetric("custom");
            else setMetric(metric === "custom" || !metric ? (applicable[0]?.key ?? "exact_match") : metric);
          };
          // Inline styles on top of the class names so the tab strip stays
          // correct even if the .prefix.css watcher hasn't picked up a newly
          // added rule — seen it miss on first edit more than once.
          return (
            <div
              className={qs("metric-tabs")}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                borderBottom: "1px solid var(--color-neutral-border)",
                background: "var(--color-neutral-surface-subtler, var(--color-neutral-background))",
              }}
            >
              <div
                role="tablist"
                aria-label={t("pages.settings.quality_settings.agreement_metric")}
                style={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}
              >
                {tabs.map((t) => {
                  const isActive = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={cn("quality-metric-tab").mod({ active: isActive }).toClassName()}
                      onClick={() => switchTab(t.key)}
                      style={{
                        appearance: "none",
                        border: "none",
                        background: "transparent",
                        padding: "10px 16px",
                        marginBottom: -1,
                        borderBottom: isActive ? "2px solid var(--color-primary-surface)" : "2px solid transparent",
                        fontSize: "var(--font-size-label)",
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? "var(--color-primary-surface)" : "var(--color-neutral-content-subtler)",
                        cursor: "pointer",
                        transition: "color 120ms, border-color 120ms",
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              {/* Recompute sits at the right of the tab row — always
                visible so the control stays in the same place, but
                disabled when the metric isn't compatible with the label
                config so an incompatible pick can't be run. Hover the
                button to see the exact reason. */}
              <Button
                type="button"
                look="outlined"
                size="small"
                onClick={recompute}
                waiting={recomputing}
                disabled={!metricCompatibility.ok || recomputing}
                aria-label={t("pages.settings.quality_settings.recompute_agreement_for_every_task")}
                title={
                  metricCompatibility.ok
                    ? "Re-score every task in this project with the current settings."
                    : metricCompatibility.reason
                }
                style={{ marginLeft: 12 }}
              >
                {recomputing ? "Recomputing…" : "Recompute all tasks"}
              </Button>
            </div>
          );
        })()}

        {/* Shared body container so all three tab panels live inside the
          same bordered card. Uniform padding makes the header → tabs →
          body read as one component. */}
        <div style={{ padding: "16px 18px 18px" }}>
          {!metric && (
            <div
              className={qs("metric-panel")}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                maxWidth: 680,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-body-small)",
                  lineHeight: 1.55,
                  color: "var(--color-neutral-content)",
                }}
              >
                {appName} picks a sensible metric per control tag in your labeling config.
              </p>
              <div
                style={{
                  marginLeft: 16,
                  paddingLeft: 12,
                  borderLeft: "3px solid var(--color-primary-surface)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: "var(--font-size-badge)",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--color-neutral-content-subtler)",
                  }}
                >
                  Selected
                </span>
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: 99,
                    background: "var(--color-primary-surface)",
                    color: "#fff",
                    fontSize: "var(--font-size-label)",
                    fontWeight: 700,
                    letterSpacing: "0.01em",
                  }}
                >
                  {applicable[0]?.label ?? "Exact match"}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-label)",
                  lineHeight: 1.55,
                  color: "var(--color-neutral-content-subtler)",
                }}
              >
                Best for getting started. Switch to <strong>Built-in</strong> to lock in one specific metric, or{" "}
                <strong>Custom Python</strong> to write your own.
              </p>
            </div>
          )}

          {metric && metric !== "custom" && (
            <div
              className={qs("metric-panel")}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxWidth: 680,
              }}
            >
              <label
                style={{
                  fontSize: "var(--font-size-badge)",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--color-neutral-content-subtler)",
                }}
              >
                Metric
              </label>
              <select
                className={qsBlock("metric-panel", "select")}
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                style={{
                  padding: "9px 12px",
                  border: "1px solid var(--color-neutral-border)",
                  borderRadius: 8,
                  fontSize: "var(--font-size-label)",
                  color: "var(--color-neutral-content)",
                  background: "var(--color-neutral-background)",
                  maxWidth: 480,
                  outline: "none",
                }}
              >
                {applicable.length > 0 && (
                  <optgroup label={t("pages.settings.quality_settings.recommended_for_this_label_config")}>
                    {applicable.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </optgroup>
                )}
                {nonApplicable.length > 0 && (
                  <optgroup label={t("pages.settings.quality_settings.other_metrics")}>
                    {nonApplicable.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-label)",
                  lineHeight: 1.55,
                  color: "var(--color-neutral-content-subtler)",
                }}
              >
                {metrics.find((m) => m.key === metric)?.description}
              </p>

              {/* Instant schema-compatibility check. Runs the moment the user
              picks a metric so they don't have to save + recompute to
              learn that the metric they chose targets Rectangles and
              their project is all Choices. */}
              {(() => {
                const spec = metrics.find((m) => m.key === metric);
                const appliesTo = spec?.applies_to ?? [];
                const matchesAny = appliesTo.includes("*");
                const controls = schema?.controls ?? [];
                const matched = matchesAny ? controls : controls.filter((c) => appliesTo.includes(c.type));
                const required = matchesAny ? [] : appliesTo;
                if (controls.length === 0) {
                  return (
                    <div style={checkBoxStyle("warn")}>
                      <strong>{t("pages.settings.quality_settings.no_controls_found")}</strong>
                      <span>{t("pages.settings.quality_settings.add_at_least_one_control_tag_to_your_lab")}</span>
                    </div>
                  );
                }
                if (matched.length > 0) {
                  return (
                    <div style={checkBoxStyle("ok")}>
                      <strong style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <IconCheck style={{ width: 14, height: 14 }} /> Compatible
                      </strong>
                      <span>
                        Will score against{" "}
                        {matched.map((c, i) => (
                          <span key={c.name}>
                            {i > 0 ? ", " : ""}
                            <code>{c.name}</code> <em style={{ opacity: 0.7 }}>({c.type})</em>
                          </span>
                        ))}
                        .
                      </span>
                    </div>
                  );
                }
                return (
                  <div style={checkBoxStyle("warn")}>
                    <strong>{t("pages.settings.quality_settings.missing_required_control")}</strong>
                    <span>
                      This metric needs {required.length === 1 ? "a " : "one of "}
                      {required.map((t, i) => (
                        <span key={t}>
                          {i > 0 ? (i === required.length - 1 ? ", or " : ", ") : ""}
                          <code>{t}</code>
                        </span>
                      ))}{" "}
                      control. Your labeling config has{" "}
                      {controls.map((c, i) => (
                        <span key={c.name}>
                          {i > 0 ? ", " : ""}
                          <code>{c.type}</code>
                        </span>
                      ))}
                      . Every pair will be skipped and agreement will be blank — pick a different metric, or adjust your
                      config.
                    </span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Embedding endpoint card — only shown when a semantic metric is
          selected. Without a URL, Semantic Similarity falls back to
          edit-distance; filling these in switches it to real cosine-of-
          embeddings. Token is optional. Timeout is a millisecond cap
          per-request (default 3 s). */}
          {isSemantic && (
            <div
              style={{
                marginTop: 14,
                padding: "14px 16px",
                border: "1px solid var(--color-neutral-border)",
                borderRadius: 10,
                background: "var(--color-neutral-surface-subtler, var(--color-neutral-background))",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                maxWidth: 680,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontSize: "var(--font-size-label)", fontWeight: 600 }}>
                    {t("pages.settings.quality_settings.embedding_endpoint")}
                  </div>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "var(--font-size-body-small)",
                      color: "var(--color-neutral-content-subtler)",
                      lineHeight: 1.5,
                    }}
                  >
                    Your HTTP endpoint that accepts <code>{'{ input: ["text"] }'}</code> and returns
                    <code>{" { embeddings: [[...]] }"}</code> {t("pages.settings.quality_settings.or_the_openai")}{" "}
                    <code>{t("pages.settings.quality_settings.dataembedding")}</code> shape). Leave blank to fall back
                    to edit-distance.
                  </p>
                </div>
                <Button
                  type="button"
                  look="outlined"
                  size="small"
                  onClick={testEmbedding}
                  waiting={embTesting}
                  disabled={!embUrl.trim() || embTesting}
                  aria-label={t("pages.settings.quality_settings.run_a_oneshot_embedding_health_check")}
                >
                  {embTesting ? "Testing…" : "Test endpoint"}
                </Button>
              </div>

              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span
                  style={{
                    fontSize: "var(--font-size-badge)",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--color-neutral-content-subtler)",
                  }}
                >
                  {t("pages.settings.quality_settings.url")}
                </span>
                <input
                  type="url"
                  value={embUrl}
                  onChange={(e) => setEmbUrl(e.target.value)}
                  placeholder="https://api.openai.com/v1/embeddings"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid var(--color-neutral-border)",
                    borderRadius: 8,
                    fontSize: "var(--font-size-label)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    background: "var(--color-neutral-background)",
                    color: "var(--color-neutral-content)",
                    outline: "none",
                  }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span
                    style={{
                      fontSize: "var(--font-size-badge)",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "var(--color-neutral-content-subtler)",
                    }}
                  >
                    {t("pages.settings.quality_settings.bearer_token_optional")}
                  </span>
                  <input
                    type="password"
                    value={embToken}
                    onChange={(e) => setEmbToken(e.target.value)}
                    placeholder={t("pages.settings.quality_settings.sk")}
                    autoComplete="off"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid var(--color-neutral-border)",
                      borderRadius: 8,
                      fontSize: "var(--font-size-label)",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                      background: "var(--color-neutral-background)",
                      color: "var(--color-neutral-content)",
                      outline: "none",
                    }}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span
                    style={{
                      fontSize: "var(--font-size-badge)",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "var(--color-neutral-content-subtler)",
                    }}
                  >
                    {t("pages.settings.quality_settings.timeout_ms")}
                  </span>
                  <input
                    type="number"
                    min={250}
                    max={30000}
                    step={250}
                    value={embTimeout}
                    onChange={(e) => setEmbTimeout(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid var(--color-neutral-border)",
                      borderRadius: 8,
                      fontSize: "var(--font-size-label)",
                      fontVariantNumeric: "tabular-nums",
                      background: "var(--color-neutral-background)",
                      color: "var(--color-neutral-content)",
                      outline: "none",
                    }}
                  />
                </label>
              </div>

              {embHealth && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: "var(--font-size-label)",
                    lineHeight: 1.5,
                    ...(embHealth.ok
                      ? {
                          background: "rgba(31, 122, 90, 0.08)",
                          border: "1px solid rgba(31, 122, 90, 0.28)",
                          color: "var(--color-positive-content, #1f7a5a)",
                        }
                      : {
                          background: "rgba(229, 72, 77, 0.08)",
                          border: "1px solid rgba(192, 53, 58, 0.28)",
                          color: "var(--color-negative-content, #c0353a)",
                        }),
                  }}
                >
                  {embHealth.ok ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                      <IconCheck style={{ width: 14, height: 14, flexShrink: 0 }} />
                      Received a {embHealth.dimensions}-dim vector in {embHealth.elapsed_ms} ms.
                      {Array.isArray(embHealth.sample) && embHealth.sample.length > 0 && (
                        <>
                          {" "}
                          {t("pages.settings.quality_settings.sample")} <code>[{embHealth.sample.join(", ")}…]</code>
                        </>
                      )}
                    </span>
                  ) : (
                    <span>
                      <strong>{t("pages.settings.quality_settings.failed")}</strong> {embHealth.error}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {!hasConfig && (
            <div style={{ marginTop: 16 }}>
              <EmptyState
                title={t("pages.settings.quality_settings.configure_your_labeling_interface_first")}
                description="The metric dropdown and the schema panel below are derived from your label config."
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      window.location.href = `/projects/${project.id}/settings/labeling`;
                    }}
                  >
                    Set up labeling interface
                  </Button>
                }
              />
            </div>
          )}

          {isCustom && (
            <>
              {/* Compact toolbar above the editor: Annotation-info pill on the
              right, optional "coverage" hint on the left if the project
              has more controls than the user's code is likely to touch.
              No section title — the active tab already says "Custom
              Python". */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 10,
                }}
              >
                {(() => {
                  // Heuristic: count distinct literal integer subscripts the
                  // user's code uses against ``result``. If they only touch
                  // ``result[0]`` but the schema has >1 control, they're
                  // probably missing some. Honest, simple, no runtime trace.
                  const controlCount = schema?.controls?.length ?? 0;
                  const indexes = new Set();
                  const rx = /\bresult\b\s*\]?\s*\[\s*(\d+)\s*\]/g;
                  let m;
                  while ((m = rx.exec(code)) !== null) indexes.add(Number(m[1]));
                  const touched = indexes.size;
                  if (controlCount > 1 && touched > 0 && touched < controlCount) {
                    return (
                      <span
                        role="status"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: "rgba(245, 166, 35, 0.12)",
                          color: "#8a5a00",
                          fontSize: "var(--font-size-body-small)",
                          lineHeight: 1.45,
                          maxWidth: 560,
                        }}
                      >
                        <span aria-hidden>⚠</span>
                        <span>
                          Your code touches <strong>{touched}</strong> of the
                          <strong> {controlCount}</strong> result items each annotation can carry (
                          {schema.controls.map((c) => c.name).join(", ")}). Unreached controls won't affect the score.
                        </span>
                      </span>
                    );
                  }
                  if (controlCount > 1 && touched === 0) {
                    return (
                      <span
                        role="status"
                        style={{
                          fontSize: "var(--font-size-body-small)",
                          color: "var(--color-neutral-content-subtler)",
                          maxWidth: 560,
                        }}
                      >
                        Annotations in this project carry <strong>{controlCount}</strong> result items (
                        {schema.controls.map((c) => c.name).join(", ")}). Iterate over
                        <code> annotation_1["result"]</code> if you want to score all of them.
                      </span>
                    );
                  }
                  return <span />; /* placeholder to keep the pill flush right */
                })()}
                <button
                  type="button"
                  onClick={() => setSchemaOpen("controls")}
                  style={pillStyle}
                  title={t("pages.settings.quality_settings.available_fields_sample_annotation_json_")}
                >
                  <span style={{ fontSize: "var(--font-size-body-small)" }}>ⓘ</span>
                  <span>{t("pages.settings.quality_settings.annotation_info")}</span>
                  {schema?.controls?.length ? (
                    <span
                      style={{
                        minWidth: 18,
                        padding: "1px 6px",
                        borderRadius: 99,
                        background: "var(--color-primary-surface)",
                        color: "#fff",
                        fontSize: "var(--font-size-body-small)",
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      {schema.controls.length}
                    </span>
                  ) : null}
                </button>
              </div>

              {/* Full-width editor. Schema reference lives in the modal dialog
              behind the "Available fields" button above. */}
              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    minWidth: 0,
                    border: "1px solid var(--color-neutral-border)",
                    borderRadius: 10,
                    background: "var(--color-neutral-background)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 14px",
                      borderBottom: "1px solid var(--color-neutral-border)",
                      background: "var(--color-neutral-surface-subtler, var(--color-neutral-emphasis))",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 99,
                        background: "rgba(37, 99, 235, 0.12)",
                        color: "var(--color-primary-surface)",
                        fontSize: "var(--font-size-body-small)",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      Python 3, sandboxed
                    </span>
                  </div>
                  {/* Fixed-height editor that scrolls internally, instead of
                  growing the whole page as lines are added. ``setSize`` on
                  mount is the reliable way to tell CodeMirror its box;
                  ``viewportMargin`` stays at the default so the virtualised
                  scroller kicks in on long files. */}
                  <CodeEditor
                    controlled
                    border={false}
                    value={code}
                    onBeforeChange={(_editor, _data, nextValue) => setCode(nextValue)}
                    editorDidMount={(editor) => editor.setSize("100%", 460)}
                    options={{
                      mode: "python",
                      theme: "default",
                      lineNumbers: true,
                      lineWrapping: false,
                      indentUnit: 4,
                      tabSize: 4,
                      indentWithTabs: false,
                      matchBrackets: true,
                      autoCloseBrackets: true,
                      smartIndent: true,
                    }}
                    className={qsBlock("editor-grid", "codemirror")}
                  />
                </div>
              </div>

              {/* Test-run action + result. The result takes its own row so
              error messages don't crowd the button on narrow widths. */}
              <div className={qs("test-row")}>
                <Button
                  type="button"
                  look="outlined"
                  size="small"
                  onClick={testRun}
                  waiting={testing}
                  aria-label={t("pages.settings.quality_settings.test_the_custom_code_on_a_real_task_pair")}
                >
                  {testing ? "Running…" : "Test on first scorable task"}
                </Button>
              </div>

              {schemaOpen && (
                <SchemaDialog schema={schema} initialTab={schemaOpen} onClose={() => setSchemaOpen(null)} />
              )}

              {testResult && (
                <div className={qs("test-result-row")}>
                  {testResult.validation && !testResult.validation.ok ? (
                    <div className={qsBlockMod("test-result", "error")}>
                      <strong>{t("pages.settings.quality_settings.rejected")}</strong>
                      <span>{testResult.validation.error}</span>
                    </div>
                  ) : testResult.runtime_error ? (
                    <div className={qsBlockMod("test-result", "error")}>
                      <strong>{(testResult.runtime_error.type || "error").toUpperCase()}</strong>
                      <span>{testResult.runtime_error.message}</span>
                      {testResult.annotations_overview?.from_names_present?.length > 0 && (
                        <span style={{ marginTop: 4, fontSize: "var(--font-size-body-small)", opacity: 0.85 }}>
                          Available fields on this task:{" "}
                          <code>{testResult.annotations_overview.from_names_present.join(", ")}</code>
                        </span>
                      )}
                    </div>
                  ) : testResult.status === "no_task" || testResult.status === "no_pair" ? (
                    <div className={qsBlockMod("test-result", "neutral")}>{testResult.message}</div>
                  ) : testResult.score == null ? (
                    <div className={qsBlockMod("test-result", "neutral")}>
                      <strong>{t("pages.settings.quality_settings.no_score")}</strong> The metric returned{" "}
                      <code>None</code> for this pair. Usually the selected metric doesn't apply to the control shape.
                      Available fields:{" "}
                      <code>{testResult.annotations_overview?.from_names_present?.join(", ") || "—"}</code>.
                    </div>
                  ) : (
                    <div className={qsBlockMod("test-result", "ok")}>
                      <span className={qsBlock("test-result", "score")}>Score {testResult.score.toFixed(4)}</span>
                      <span className={qsBlock("test-result", "meta")}>
                        task #{testResult.task_id}, annotation {testResult.pair?.annotation_1} vs{" "}
                        {testResult.pair?.annotation_2}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {/* /metric-card body */}
      </div>
      {/* /metric-card */}

      <div className={qs("divider")} />

      {/* Step 2 — only relevant once Step 1 (metric) is chosen. */}
      <h3 style={{ margin: "0 0 4px" }}>
        <span style={{ ...badgeFlowStyle("method"), marginRight: 8 }}>
          {t("pages.settings.quality_settings.step_2")}
        </span>
        Methodology
        <HelpTip>
          How per-pair scores get rolled up across all annotators on a task. Pairwise averaging is the simplest choice;
          kappa/alpha coefficients chance-correct for baseline agreement.
        </HelpTip>
      </h3>
      <p className={settingsDescription} style={{ marginTop: 0 }}>
        Roll-up rule across all annotators on a task.
      </p>
      <RadioCardGroup
        name="agreement_method"
        value={method}
        onChange={setMethod}
        options={AGREEMENT_OPTIONS.map((o) => ({ ...o, applies: METHOD_APPLIES[o.value] }))}
      />
    </Section>
  );
};

// ── 5. Low-Agreement Resolution ────────────────────────────────────────────────

const LowAgreementSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const initialStrategy = project.low_agreement_strategy ?? "none";
  const [strategy, setStrategy] = useState(initialStrategy);
  const [threshold, setThreshold] = useState(project.low_agreement_threshold ?? 0.6);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStrategy(project.low_agreement_strategy ?? "none");
    setThreshold(project.low_agreement_threshold ?? 0.6);
  }, [project.id, project.low_agreement_strategy, project.low_agreement_threshold]);

  const enabled = strategy !== "none";
  const dirty = strategy !== initialStrategy || (enabled && threshold !== (project.low_agreement_threshold ?? 0.6));

  const save = useCallback(async () => {
    setSaving(true);
    const res = await updateProject({
      low_agreement_strategy: strategy,
      low_agreement_threshold: enabled ? Math.max(0, Math.min(1, Number(threshold) || 0)) : null,
    });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.quality_settings.resolution_strategy_saved") });
      onSaved?.();
    } else toast.show({ message: "Failed to save", type: "error" });
  }, [strategy, threshold, enabled, updateProject, toast, onSaved]);

  const summary =
    strategy === "none"
      ? "No low agreement resolution strategy configured."
      : strategy === "assign_additional"
        ? `Assigns the next available annotator when task agreement < ${Math.round(threshold * 100)}%.`
        : `Moves tasks below ${Math.round(threshold * 100)}% agreement to the review queue.`;

  return (
    <Section
      title={t("pages.settings.quality_settings.low_agreement_resolution")}
      description={t("pages.settings.quality_settings.enable_a_resolution_strategy_for_tasks_t")}
      docsHref="https://afriannotate.waraka.org/docs/review-and-quality/agreement#low-agreement-resolution"
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={enabled}
        onChange={(on) => setStrategy(on ? "assign_additional" : "none")}
        label={t("pages.settings.quality_settings.assign_additional_annotator")}
        description={t("pages.settings.quality_settings.assigns_the_next_available_annotator_in_")}
      />
      {enabled && (
        <div className={qs("row")} style={{ marginTop: 12 }}>
          <div className={qs("field")}>
            <label className={qsBlock("field", "label")}>{t("pages.settings.quality_settings.strategy")}</label>
            <select className={qs("input")} value={strategy} onChange={(e) => setStrategy(e.target.value)}>
              <option value="assign_additional">
                {t("pages.settings.quality_settings.assign_additional_annotator")}
              </option>
              <option value="review_queue">{t("pages.settings.quality_settings.move_to_review_queue")}</option>
            </select>
          </div>
          <div className={qs("field")}>
            <label className={qsBlock("field", "label")}>
              {t("pages.settings.quality_settings.minimum_agreement")}
            </label>
            <SliderRow value={threshold} onChange={setThreshold} min={0} max={1} step={0.05} />
          </div>
        </div>
      )}
    </Section>
  );
};

// ── 6. Honeypots (gold) ────────────────────────────────────────────────────────

const HoneypotSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [ratio, setRatio] = useState(project.honeypot_ratio ?? 10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRatio(project.honeypot_ratio ?? 10);
  }, [project.id, project.honeypot_ratio]);

  const effective = Math.max(0, Math.min(100, Number(ratio) || 0));
  const dirty = effective !== (project.honeypot_ratio ?? 10);

  const save = useCallback(async () => {
    setSaving(true);
    const res = await updateProject({ honeypot_ratio: effective });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({
        message: t("pages.settings.quality_settings.honeypot_ratio_saved", undefined, "Honeypot ratio saved"),
      });
      onSaved?.();
    } else toast.show({ message: "Failed to save", type: "error" });
  }, [effective, updateProject, toast, onSaved, t]);

  const summary =
    effective === 0
      ? "No honeypots targeted for this project."
      : `Aim for ~${effective}% of tasks to be blind honeypots (gold).`;

  return (
    <Section
      title={t("pages.settings.quality_settings.honeypots", undefined, "Honeypots (gold)")}
      description={t(
        "pages.settings.quality_settings.honeypots_description",
        undefined,
        "Upload gold answers from the Import page to seed honeypot tasks. This target guides how many you inject; honeypots stay invisible to annotators and drive their real-time score.",
      )}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <div className={qs("field")}>
        <label className={qsBlock("field", "label")}>
          {t("pages.settings.quality_settings.target_honeypot_percentage", undefined, "Target honeypot percentage")}
        </label>
        <p className={settingsCaption} style={{ marginTop: 0 }}>
          Informational target — honeypots are added by importing gold answers, not auto-sampled.
        </p>
        <input
          type="number"
          min={0}
          max={100}
          className={qs("input")}
          value={ratio}
          onChange={(e) => setRatio(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
        />
      </div>
    </Section>
  );
};

// ── Page root ──────────────────────────────────────────────────────────────────

export const QualitySettings = () => {
  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Quality"]));

  const refresh = useCallback(() => {
    if (project?.id) fetchProject(project.id, true);
  }, [project?.id, fetchProject]);

  // An extension may say that sections do not apply to this project
  // (``project.extensions.hidden_settings``, as "quality:<key>").
  const hidden = project?.extensions?.hidden_settings;
  const sections = useMemo(
    () =>
      [
        <OverlapSection key="overlap" project={project} onSaved={refresh} />,
        <TasksPerAnnotatorSection key="limit" project={project} onSaved={refresh} />,
        <EvaluationSection key="eval" project={project} onSaved={refresh} />,
        <AgreementSection key="agree" project={project} onSaved={refresh} />,
        <LowAgreementSection key="low" project={project} onSaved={refresh} />,
        <HoneypotSection key="honeypot" project={project} onSaved={refresh} />,
      ].filter((section) => !(hidden ?? []).includes(`quality:${section.key}`)),
    [project, refresh, hidden],
  );

  if (!project?.id) return null;

  return (
    <div className={`${qualityRoot} ${cn("settings-page").toClassName()}`}>
      <SettingsPageHeader
        title="Quality"
        subtitle={t("pages.settings.quality_settings.control_how_annotations_are_distributed_")}
      />
      {sections}
    </div>
  );
};

QualitySettings.title = "Quality";
QualitySettings.path = "/quality";
